import { useEffect, useRef, useState } from "react";
import { getLocaleDisplayName } from "@expertloop/i18n";
import { useI18n } from "@expertloop/i18n/react";
import "./LanguageSwitcher.css";

// Sélecteur de langue de l'Admin : mêmes comportements que le Web
// (langues connues, états de traduction, locale navigateur sur demande).
export function LanguageSwitcher() {
	const {
		locale,
		renderedLocale,
		browserLocale,
		locales,
		localeStatus,
		pendingLocale,
		t,
		setLocale,
		requestLocale,
		refreshLocales,
	} = useI18n();
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;

		const onPointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("pointerdown", onPointerDown);
		return () => document.removeEventListener("pointerdown", onPointerDown);
	}, [open]);

	const toggleOpen = () => {
		if (!open) void refreshLocales();
		setOpen((value) => !value);
	};

	const selectLocale = (next: string) => {
		setLocale(next);
		setOpen(false);
	};

	const askTranslation = (target: string) => {
		void requestLocale(target);
		setOpen(false);
	};

	const shouldOfferBrowserLocale = () =>
		browserLocale !== null &&
		browserLocale !== locale &&
		!locales.some((entry) => entry.locale === browserLocale) &&
		pendingLocale !== browserLocale;

	const statusHint = (status: string, isPending: boolean): string | null => {
		if (isPending || status === "waiting_trad" || status === "translating") {
			return t("language.translationInProgress");
		}
		if (status === "translation_failed") {
			return t("language.translationFailed");
		}
		return null;
	};

	const currentInProgress =
		pendingLocale !== null || localeStatus === "waiting_trad" || localeStatus === "translating";

	return (
		<div className="language-switcher" ref={rootRef}>
			<button
				type="button"
				className="language-switcher-btn"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-label={t("language.selectorAria")}
				title={getLocaleDisplayName(renderedLocale)}
				onClick={toggleOpen}
			>
				<span aria-hidden="true">🌐</span>
				<span className="language-switcher-code">{renderedLocale.toUpperCase()}</span>
				{currentInProgress && <span className="language-switcher-spinner" aria-hidden="true" />}
			</button>

			{open && (
				<ul className="language-switcher-panel" role="listbox" aria-label={t("language.selectorAria")}>
					{locales.map((entry) => {
						const isPending = pendingLocale === entry.locale;
						// Un échec de mise à jour d'un catalogue déjà utilisable n'est pas à signaler.
						const hint = statusHint(entry.status === "translation_failed" && entry.version > 0 ? "ready" : entry.status, isPending);
						// Un catalogue existant reste utilisable même si sa dernière mise à jour a échoué.
						const selectable = entry.status === "ready" || entry.isSource || entry.version > 0;
						return (
							<li key={entry.locale} role="option" aria-selected={entry.locale === locale}>
								<button
									type="button"
									className={`language-switcher-option${entry.locale === locale ? " is-active" : ""}`}
									disabled={!selectable && !isPending}
									onClick={() =>
										selectable ? selectLocale(entry.locale) : askTranslation(entry.locale)
									}
								>
									<span className="language-switcher-label">
										{entry.label || getLocaleDisplayName(entry.locale)}
									</span>
									{entry.locale === locale && <span aria-hidden="true">✓</span>}
									{hint && <span className="language-switcher-hint">{hint}</span>}
								</button>
							</li>
						);
					})}

					{shouldOfferBrowserLocale() && browserLocale && (
						<>
							<li role="separator" className="language-switcher-separator" />
							<li role="option" aria-selected={false}>
								<button
									type="button"
									className="language-switcher-option"
									onClick={() => askTranslation(browserLocale)}
								>
									<span className="language-switcher-label">
										{getLocaleDisplayName(browserLocale)}
									</span>
									<span className="language-switcher-hint">{t("language.availableOnRequest")}</span>
								</button>
							</li>
						</>
					)}
				</ul>
			)}
		</div>
	);
}
